import { useCallback, useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { RoomEvent } from 'livekit-client'

import { useRoomContext } from '@/src/context'
import { EReaction } from '@/src/constants/reactions'
import { handlePublishData } from '@/src/helpers/handlePublishData'
import { sendRaiseHandRequest } from '@/src/helpers/raiseHandRequest'

import { useAppDispatch, useAppSelector } from '@/src/store/hooks'
import {
  selectCurrentRaiseHand,
  setCurrentRaiseHand,
  setParticipantHand,
  addParticipantEphemeralReaction,
} from '@/src/store/slices/reactions'
import { selectRoomState } from '@/src/store/slices/room'

interface UseReactionsOptions {
  /** When true, raise-hand uses only the server API (no data channel / no canPublishData needed). */
  apiOnly?: boolean
  /**
   * Перед поднятием руки: включить камеру и микрофон.
   * Если вернёт false, в очередь не пишем.
   */
  beforeRaise?: () => Promise<boolean>
}

export const useReactions = (options?: UseReactionsOptions) => {
  const apiOnly = options?.apiOnly ?? false
  const lastReactionTime = useRef(0)

  const dispatch = useAppDispatch()
  const room = useRoomContext()
  const router = useRouter()
  const isMega = router.pathname === '/mega/[roomName]'
  const broadcastName = isMega && typeof router.query.roomName === 'string' ? router.query.roomName : undefined
  const { livekit_url } = useAppSelector(selectRoomState)
  const handRequestPending = useRef(false)
  const [isUpdatingHand, setIsUpdatingHand] = useState(false)

  const isHandRaised = useAppSelector(selectCurrentRaiseHand)

  useEffect(() => {
    if (!isMega || !room) return
    const syncHand = () => {
      if (handRequestPending.current || !room.localParticipant.identity) return
      const raised = room.localParticipant.attributes?.handRaised === 'true'
      dispatch(setCurrentRaiseHand(raised))
      dispatch(setParticipantHand({ identity: room.localParticipant.identity, isHandRaised: raised }))
    }
    syncHand()
    room.on(RoomEvent.Connected, syncHand)
    room.on(RoomEvent.Reconnected, syncHand)
    room.on(RoomEvent.ParticipantAttributesChanged, syncHand)
    return () => {
      room.off(RoomEvent.Connected, syncHand)
      room.off(RoomEvent.Reconnected, syncHand)
      room.off(RoomEvent.ParticipantAttributesChanged, syncHand)
    }
  }, [dispatch, isMega, room])

  const handleRaiseHandToggle = useCallback(async () => {
    const myIdentity = room?.localParticipant?.identity
    if (isMega && (handRequestPending.current || !broadcastName || !livekit_url || !myIdentity || !room?.name)) return
    const newValue = !isHandRaised

    if (isMega && newValue && options?.beforeRaise) {
      const ready = await options.beforeRaise()

      if (!ready) return
    }

    dispatch(setCurrentRaiseHand(newValue))

    if (myIdentity) {
      dispatch(setParticipantHand({ identity: myIdentity, isHandRaised: newValue }))
    }

    if (isMega) {
      handRequestPending.current = true
      setIsUpdatingHand(true)
      try {
        await sendRaiseHandRequest({ room: room.name, identity: myIdentity, isHandRaised: newValue, livekitUrl: livekit_url, broadcastName })
      } catch (err) {
        dispatch(setCurrentRaiseHand(isHandRaised))
        dispatch(setParticipantHand({ identity: myIdentity, isHandRaised }))
        console.error('Failed to update hand raise queue:', err)
      } finally {
        handRequestPending.current = false
        setIsUpdatingHand(false)
      }
      return
    }

    if (!apiOnly) {
      handlePublishData(
        room,
        {
          type: 'raiseHand',
          isHandRaised: newValue,
          participant: myIdentity,
        },
        {
          reliable: true,
          topic: 'reaction-topic',
        },
      )
    }

    if (livekit_url && myIdentity && room?.name) {
      sendRaiseHandRequest({
        room: room.name, identity: myIdentity, isHandRaised: newValue, livekitUrl: livekit_url,
      }).catch((err) => {
        console.error('Failed to update raise hand via API:', err)
      })
    }
  }, [dispatch, isHandRaised, room, livekit_url, apiOnly, isMega, broadcastName, options?.beforeRaise])

  const handleQuickReaction = useCallback(
    (reaction: keyof typeof EReaction) => {
      const now = Date.now()
      if (now - lastReactionTime.current < 300) return
      lastReactionTime.current = now

      const myIdentity = room?.localParticipant?.identity
      if (!myIdentity) return

      const reactionId = Math.random().toString()

      dispatch(
        addParticipantEphemeralReaction({
          identity: myIdentity,
          ephemeral: {
            id: reactionId,
            type: reaction,
          },
        }),
      )

      handlePublishData(
        room,
        {
          type: 'ephemeral',
          reactionId,
          reaction,
          participant: myIdentity,
        },
        {
          reliable: true,
          topic: 'reaction-topic',
        },
      )
    },
    [dispatch, room],
  )

  return { handleRaiseHandToggle, handleQuickReaction, isHandRaised, isUpdatingHand }
}
